// src/pages/admin/editorTabs/UsersTab.jsx
import React, { useEffect, useState } from "react";
import { pagesApi } from "../../../api/pagesApi";
import ConfirmDeleteModal from "../../../components/GeneralComponents/ConfirmDeleteModal";

export default function UsersTab({ pageId, onToast, t }) {
  const [managerEmail, setManagerEmail] = useState("");
  const [managerPassword, setManagerPassword] = useState("");

  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [managers, setManagers] = useState([]);

  // delete modal state
  const [pendingDelete, setPendingDelete] = useState(null); // { id, email }

  // change password modal state
  const [pendingPassword, setPendingPassword] = useState(null); // { id, email }
  const [newPassword, setNewPassword] = useState("");
  const [updatingPasswordId, setUpdatingPasswordId] = useState(null);

  const toast = (payload) => {
    if (typeof onToast === "function") onToast(payload);
  };

  const loadManagers = async () => {
    if (!pageId) return;
    try {
      setLoadingList(true);
      const list = await pagesApi.listManagers(pageId);
      setManagers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setManagers([]);
      toast({
        type: "error",
        title: "Load failed",
        message: e?.message || "Failed to load managers list.",
      });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const createManager = async () => {
    const email = managerEmail.trim();
    const password = managerPassword;

    if (!email || !password) {
      toast({ type: "error", title: "Missing info", message: "Email and password are required." });
      return;
    }

    try {
      setCreating(true);
      await pagesApi.createManager(pageId, { email, password });

      toast({
        type: "success",
        title: "Manager created",
        message: `${email} was added successfully.`,
      });

      setManagerEmail("");
      setManagerPassword("");
      await loadManagers();
    } catch (e) {
      toast({
        type: "error",
        title: "Create failed",
        message: e?.message || "Failed to create manager.",
      });
    } finally {
      setCreating(false);
    }
  };

  // ------- Delete manager -------
  const requestDeleteManager = (m) => setPendingDelete({ id: m.id, email: m.email });

  const confirmDeleteManager = async () => {
    if (!pendingDelete?.id) return;

    try {
      setDeletingId(pendingDelete.id);
      await pagesApi.deleteManager(pageId, pendingDelete.id);

      toast({
        type: "success",
        title: "Manager deleted",
        message: `${pendingDelete.email} was removed successfully.`,
      });

      setPendingDelete(null);
      await loadManagers();
    } catch (e) {
      toast({
        type: "error",
        title: "Delete failed",
        message: e?.message || "Failed to delete manager.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ------- Change password -------
  const openChangePassword = (m) => {
    setPendingPassword({ id: m.id, email: m.email });
    setNewPassword("");
  };

  const confirmChangePassword = async () => {
    if (!pendingPassword?.id) return;

    const pass = String(newPassword || "");
    if (!pass || pass.length < 6) {
      toast({
        type: "error",
        title: "Invalid password",
        message: "Password must be at least 6 characters.",
      });
      return;
    }

    try {
      setUpdatingPasswordId(pendingPassword.id);
      await pagesApi.updateManagerPassword(pageId, pendingPassword.id, { password: pass });

      toast({
        type: "success",
        title: "Password updated",
        message: `Password updated for ${pendingPassword.email}.`,
      });

      setPendingPassword(null);
      setNewPassword("");
    } catch (e) {
      toast({
        type: "error",
        title: "Update failed",
        message: e?.message || "Failed to update password.",
      });
    } finally {
      setUpdatingPasswordId(null);
    }
  };

  const isBusy = creating || !!deletingId || !!updatingPasswordId;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-5">
      <div className="font-bold text-gray-900">Users (Managers)</div>

      {/* Create manager */}
      <div className="border rounded-xl p-4">
        <div className="font-semibold text-gray-900 mb-3">Create manager</div>

        <input
          placeholder="Manager email"
          value={managerEmail}
          onChange={(e) => setManagerEmail(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-2"
        />

        <input
          placeholder="Password"
          type="password"
          value={managerPassword}
          onChange={(e) => setManagerPassword(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-3"
        />

        <button
          onClick={createManager}
          disabled={creating}
          className="w-full py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {creating ? "Creating..." : "Create Manager"}
        </button>
      </div>

      {/* Managers list */}
      <div className="border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-gray-900">Managers list</div>
          <button
            onClick={loadManagers}
            disabled={loadingList}
            className="px-3 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingList ? "Loading..." : "Refresh"}
          </button>
        </div>

        {managers.length === 0 ? (
          <div className="text-sm text-gray-500">No managers yet.</div>
        ) : (
          <div className="space-y-2">
            {managers.map((m) => (
              <div
                key={m.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{m.email}</div>
                  <div className="text-xs text-gray-500">role: {m.role}</div>
                </div>

                <div className="flex flex-wrap gap-2 sm:justify-end">
                  <button
                    onClick={() => openChangePassword(m)}
                    disabled={isBusy}
                    className="px-3 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
                  >
                    Change Password
                  </button>

                  <button
                    onClick={() => requestDeleteManager(m)}
                    disabled={isBusy}
                    className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === m.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDeleteModal
        open={!!pendingDelete}
        t={t}
        title={t?.("usersTab.deleteManagerTitle", "Delete manager?")}
        message={
          pendingDelete?.email
            ? `${t?.("usersTab.deleteManagerMessagePrefix", "Are you sure you want to delete")} ${pendingDelete.email}? ${t?.(
              "usersTab.deleteManagerMessageSuffix",
              "This action cannot be undone."
            )}`
            : t?.(
              "usersTab.deleteManagerMessage",
              "Are you sure you want to delete this manager? This action cannot be undone."
            )
        }
        confirmText={t?.("usersTab.deleteManagerConfirm", "Delete")}
        cancelText={t?.("usersTab.deleteManagerCancel", "Cancel")}
        loading={!!deletingId}
        onClose={() => {
          if (deletingId) return;
          setPendingDelete(null);
        }}
        onConfirm={confirmDeleteManager}
      />

      {/* Change password modal (simple in-app modal, not browser popup) */}
      {!!pendingPassword && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border p-5">
            <div className="font-bold text-gray-900">Change manager password</div>
            <div className="text-sm text-gray-600 mt-1 truncate">
              Manager: <span className="font-medium">{pendingPassword.email}</span>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-2 w-full px-3 py-2 border rounded-lg"
                placeholder="At least 6 characters"
              />
            </div>

            <div className="mt-5 flex gap-2">
              <button
                onClick={() => {
                  if (updatingPasswordId) return;
                  setPendingPassword(null);
                  setNewPassword("");
                }}
                disabled={!!updatingPasswordId}
                className="flex-1 px-3 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={confirmChangePassword}
                disabled={!!updatingPasswordId}
                className="flex-1 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {updatingPasswordId ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}